import { useEffect, useState } from "react";
import axios from "axios";
import { useParams, Link, Navigate } from "react-router-dom";
import * as Lucide from "lucide-react";
import { ministries as mockMinistries } from "../mock";
import { ArrowLeft, ArrowRight, Calendar, MapPin, Users, CheckCircle2, Quote, Shirt } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Normalize ministry shape (API uses snake_case, mock uses camelCase)
function normalize(m) {
  if (!m) return null;
  return {
    slug: m.slug,
    title: m.title,
    desc: m.desc,
    icon: m.icon,
    hero: m.hero,
    tagline: m.tagline,
    verse: m.verse || { text: m.verse_text || "", reference: m.verse_reference || "" },
    when: m.when,
    where: m.where,
    whoFor: m.whoFor || m.who_for || "",
    whatToExpect: m.whatToExpect || m.what_to_expect || [],
    dressCode: m.dressCode || m.dress_code || "",
    cta: m.cta || { label: m.cta_label || "Learn More", href: m.cta_href || "/#visit" },
  };
}

export default function MinistryPage() {
  const { slug } = useParams();
  const [m, setM] = useState(() => normalize(mockMinistries.find(x => x.slug === slug)));
  const [all, setAll] = useState(mockMinistries.map(normalize));
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    axios.get(`${API}/ministries/${slug}`)
      .then(r => setM(normalize(r.data)))
      .catch(err => { if (err?.response?.status === 404) setNotFound(true); });
    axios.get(`${API}/ministries`).then(r => { if (r.data?.length) setAll(r.data.map(normalize)); }).catch(() => {});
  }, [slug]);

  if (notFound && !m) return <Navigate to="/" replace />;
  if (!m) return null;
  const Icon = Lucide[m.icon] || Lucide.Heart;
  const ctaExternal = m.cta.href.startsWith("http") || m.cta.href.startsWith("mailto:");

  return (
    <main className="pt-24 bg-[#faf7f2]">
      <section className="relative h-[68vh] min-h-[460px] w-full overflow-hidden">
        <img src={m.hero} alt={m.title} className="absolute inset-0 h-full w-full object-cover kenburns" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 h-full max-w-6xl mx-auto px-6 md:px-12 flex flex-col justify-end pb-20">
          <Link to="/" className="inline-flex items-center gap-2 text-[#c8a36a] hover:text-[#f0d9a8] text-sm mb-8 w-fit">
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
          <div className="flex items-center gap-3 text-[#c8a36a] mb-4">
            <div className="h-10 w-10 rounded-full bg-[#c8a36a]/20 flex items-center justify-center border border-[#c8a36a]/40">
              <Icon className="h-5 w-5 text-[#f0d9a8]" />
            </div>
            <span className="uppercase tracking-[0.32em] text-xs font-semibold">Ministry</span>
          </div>
          <h1 className="font-serif-display text-white text-5xl md:text-7xl leading-[1.02]">{m.title}</h1>
          <p className="mt-5 font-script text-2xl md:text-3xl text-[#f0d9a8] italic">{m.tagline}</p>
        </div>
        <div className="absolute bottom-0 inset-x-0 water-line" />
      </section>

      <section className="max-w-5xl mx-auto px-6 md:px-12 py-20">
        <div className="grid lg:grid-cols-[1.6fr_1fr] gap-12 items-start">
          <div>
            <div className="text-[#0e5469] uppercase tracking-[0.32em] text-xs font-semibold mb-3">Welcome</div>
            <h2 className="font-serif-display text-3xl md:text-4xl text-[#0a2d38] leading-[1.1]">
              We&rsquo;re so glad <span className="font-script italic text-[#c8a36a]">you&rsquo;re here.</span>
            </h2>
            <p className="mt-6 text-[#3b4d54] leading-relaxed text-[17px]">{m.desc}</p>
            {m.whoFor && <p className="mt-4 text-[#3b4d54] leading-relaxed text-[17px]">{m.whoFor}</p>}
          </div>

          {m.verse?.text && (
            <div className="bg-white border border-[#e7dfd0] rounded-md p-7 relative">
              <Quote className="h-5 w-5 text-[#c8a36a] mb-3" />
              <p className="font-script text-xl md:text-2xl text-[#1c2b30] leading-snug">&ldquo;{m.verse.text}&rdquo;</p>
              <div className="mt-4 text-xs uppercase tracking-[0.26em] text-[#0e5469] font-semibold">{m.verse.reference}</div>
            </div>
          )}
        </div>
      </section>

      <section className="bg-[#f3ede0] py-16">
        <div className="max-w-5xl mx-auto px-6 md:px-12 grid md:grid-cols-3 gap-5">
          <InfoTile icon={Calendar} label="When" value={m.when} />
          <InfoTile icon={MapPin} label="Where" value={m.where} />
          <InfoTile icon={Users} label="Who it's for" value={(m.whoFor || "").split(".")[0] + "."} />
        </div>
      </section>

      {m.whatToExpect?.length > 0 && (
        <section className="max-w-5xl mx-auto px-6 md:px-12 py-20">
          <div className="text-[#0e5469] uppercase tracking-[0.32em] text-xs font-semibold mb-3">What to expect</div>
          <h2 className="font-serif-display text-3xl md:text-4xl text-[#0a2d38] leading-[1.1]">
            Here&rsquo;s what <span className="font-script italic text-[#c8a36a]">a typical time looks like.</span>
          </h2>
          <ul className="mt-10 space-y-4">
            {m.whatToExpect.map((item, i) => (
              <li key={i} className="flex items-start gap-4 bg-white border border-[#e7dfd0] rounded-md p-5 card-lift">
                <div className="h-8 w-8 rounded-full bg-[#0e5469] text-white flex items-center justify-center flex-shrink-0 font-serif-display text-sm">
                  {i + 1}
                </div>
                <p className="text-[#3b4d54] leading-relaxed pt-1">{item}</p>
              </li>
            ))}
          </ul>

          {m.dressCode && (
            <div className="mt-10 flex items-start gap-3 p-5 bg-[#faf7f2] border border-[#e7dfd0] rounded-md">
              <Shirt className="h-5 w-5 text-[#c8a36a] mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-semibold text-[#0a2d38]">What to wear</div>
                <div className="text-sm text-[#5b6c72] mt-0.5">{m.dressCode}</div>
              </div>
            </div>
          )}
        </section>
      )}

      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0e5469] via-[#0a4253] to-[#0a2d38]" />
        <div className="absolute -top-20 -right-20 h-72 w-72 rounded-full bg-[#c8a36a]/15 blur-3xl" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center text-white">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#c8a36a]/40 text-[#f0d9a8] text-xs uppercase tracking-[0.28em] font-semibold mb-6">
            <CheckCircle2 className="h-3.5 w-3.5" /> You&rsquo;re Invited
          </div>
          <h2 className="font-serif-display text-4xl md:text-5xl leading-[1.05]">
            Ready to <span className="font-script italic text-[#f0d9a8]">take the next step?</span>
          </h2>
          <p className="mt-5 text-white/80 max-w-xl mx-auto leading-relaxed">
            No pressure, no performance. Just a family that would love to know you.
          </p>
          <div className="mt-9 flex flex-wrap justify-center gap-3">
            {ctaExternal ? (
              <a href={m.cta.href} target={m.cta.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer"
                className="btn-gold rounded-full px-8 py-4 font-semibold inline-flex items-center gap-2">
                {m.cta.label} <ArrowRight className="h-4 w-4" />
              </a>
            ) : (
              <a href={m.cta.href} className="btn-gold rounded-full px-8 py-4 font-semibold inline-flex items-center gap-2">
                {m.cta.label} <ArrowRight className="h-4 w-4" />
              </a>
            )}
            <Link to="/" className="rounded-full px-8 py-4 font-semibold border border-white/30 hover:bg-white/10 transition-colors">
              Back to Home
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 md:px-12 py-20">
        <div className="text-center mb-10">
          <div className="text-[#0e5469] uppercase tracking-[0.32em] text-xs font-semibold mb-3">Explore more</div>
          <h2 className="font-serif-display text-3xl md:text-4xl text-[#0a2d38]">Other ministries you might love</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {all.filter(x => x.slug !== m.slug).slice(0, 3).map(x => {
            const XIcon = Lucide[x.icon] || Lucide.Heart;
            return (
              <Link key={x.slug} to={`/ministries/${x.slug}`} className="group bg-white border border-[#e7dfd0] rounded-md p-6 card-lift">
                <div className="h-10 w-10 rounded-full bg-[#0e5469] flex items-center justify-center mb-4 group-hover:bg-[#c8a36a] transition-colors">
                  <XIcon className="h-4 w-4 text-[#f0d9a8] group-hover:text-[#0e5469]" />
                </div>
                <div className="font-serif-display text-xl text-[#0a2d38]">{x.title}</div>
                <div className="mt-2 text-sm text-[#5b6c72] line-clamp-2">{x.desc}</div>
                <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0e5469] group-hover:text-[#c8a36a]">
                  Learn more <ArrowRight className="h-3.5 w-3.5" />
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    </main>
  );
}

function InfoTile({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-[#e7dfd0] rounded-md p-6">
      <div className="flex items-center gap-2 text-[#c8a36a] uppercase tracking-[0.22em] text-[10px] font-semibold">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className="mt-3 font-serif-display text-xl text-[#0a2d38] leading-snug">{value}</div>
    </div>
  );
}
